"use client";

import React, { useState } from 'react';
import { usePathname } from 'next/navigation';
import { getFormattedVersion } from '../../utils/version';

export default function SearchPrompt() {
  const pathname = usePathname();
  const isDetailPage = pathname.includes('/restaurant/');
  const [isExpanded, setIsExpanded] = useState(false);
  
  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };
  
  return (
    <div 
      className={`fixed ${isExpanded ? 'w-full left-0 px-0' : 'w-full md:max-w-4xl px-4'} z-30`}
      style={{ 
        // Centered at bottom with fixed position
        bottom: '0', 
        left: isExpanded ? '0' : '50%',
        marginLeft: isExpanded ? '0' : '9.2rem',
        // Remove the bottom margin
        marginBottom: '0',
        transform: isExpanded ? 'none' : 'translateX(-50%)',
      }}
    >
      <div className="relative w-full">
        {/* Mobile collapsed search button (only visible when not expanded) */}
        {!isExpanded && (
          <div className="md:hidden flex justify-center">
            <button 
              onClick={toggleExpand}
              className="w-12 h-12 rounded-full bg-primary text-white shadow-lg flex items-center justify-center hover:bg-opacity-90 transition-all duration-300 focus:outline-none mb-4"
              aria-label="Search"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
          </div>
        )}
        
        {/* Full search input (always visible on desktop, visible on mobile only when expanded) */}
        <div 
          className={`${isExpanded ? 'flex' : 'hidden md:flex'} items-center w-full bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-all duration-300 focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent overflow-hidden ${isExpanded ? 'mx-0 rounded-none md:rounded-xl' : ''}`}
        >
          {/* Left side - Input */}
          <input
            type="text"
            className="w-full pl-4 py-3 md:py-3.5 border-none focus:outline-none text-gray-700 text-sm md:text-base"
            placeholder="Search for restaurants, cuisines or dishes..."
            autoFocus={isExpanded}
          />
          
          {/* Right side - Quick tags (hidden on small mobile) */}
          <div className="hidden sm:flex items-center pr-2 pl-1 border-l border-gray-100">
            <div className="flex items-center p-2 text-lg cursor-pointer rounded-full hover:bg-gray-50 transition-colors" title="Pizza">
              <span>🍕</span>
            </div>
            <div className="flex items-center p-2 text-lg cursor-pointer rounded-full hover:bg-gray-50 transition-colors" title="Burgers">
              <span>🍔</span>
            </div>
            <div className="flex items-center p-2 text-lg cursor-pointer rounded-full hover:bg-gray-50 transition-colors" title="Coffee">
              <span>☕</span>
            </div>
            <div className="flex items-center p-2 text-lg cursor-pointer rounded-full hover:bg-gray-50 transition-colors" title="Biryani">
              <span>🥘</span>
            </div>
          </div>
          
          {/* Send button with close functionality on mobile */}
          {isExpanded ? (
            <div className="flex">
              <button 
                className="bg-primary text-white p-3 hover:bg-opacity-90 transition-colors"
                aria-label="Search"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M22 2L11 13"></path>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M22 2L15 22L11 13L2 9L22 2Z"></path>
                </svg>
              </button>
              <button 
                className="bg-gray-100 p-3 md:hidden" 
                onClick={toggleExpand}
                aria-label="Close search"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <button className="bg-primary text-white p-3 md:p-3.5 hover:bg-opacity-90 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M22 2L11 13"></path>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M22 2L15 22L11 13L2 9L22 2Z"></path>
              </svg>
            </button>
          )}
        </div>
      </div>
      
      {/* Slogan on home page, disclaimer on detail page - hidden on mobile */}
      <div className="hidden md:flex text-center mt-3 text-xs text-gray-400 px-6 items-center justify-center flex-wrap">
        <span className="font-medium text-primary">OpenMenu.pk</span>
        <span className="mx-1">—</span>
        <span>Your AI-powered food ordering assistant.</span>
        <span className="mx-1">•</span>
        <span className="text-gray-300">{getFormattedVersion()}</span>
        
        {isDetailPage && (
          <>
            <span className="mx-1">•</span>
            <span>Verify hours and menu items before ordering.</span>
          </>
        )}
      </div>
      
      {/* Overlay to close expanded search on mobile */}
      {isExpanded && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-20 md:hidden z-[-1]" 
          onClick={toggleExpand}
          aria-hidden="true"
        />
      )}
    </div>
  );
} 